import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, Zap, Crown, Lock, X, Sparkles, Users, ChevronRight, Star, Shield, BarChart3, Mail, Eye, UserPlus } from 'lucide-react';
import { useUserCard } from '../hooks/useUserCard';
import PaymentModal from '../components/PaymentModal';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { userCard, loading: cardLoading } = useUserCard();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [pricingEnabled, setPricingEnabled] = useState(true);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [businessMemberCount, setBusinessMemberCount] = useState(3);
  const [subscribingPremium, setSubscribingPremium] = useState(false);
  const [showCoupons, setShowCoupons] = useState(false);

  useEffect(() => {
    const savedState = localStorage.getItem('pricingEnabled');
    if (savedState !== null) {
      setPricingEnabled(JSON.parse(savedState));
    }
  }, []);

  useEffect(() => {
    const loadCoupons = async () => {
      try {
        const { couponAPI } = await import('../services/api');
        const coupons = await couponAPI.getAvailableCoupons();
        setAvailableCoupons(coupons);
      } catch (error) {
        console.error('Error loading coupons:', error);
      }
    };
    loadCoupons();
  }, []);

  const plans = {
    free: {
      name: 'Free',
      description: 'Perfect for getting started',
      icon: Lock,
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-500',
      borderColor: 'border-slate-200',
      isFree: true,
      monthly: { price: 0, planId: 'free' },
      annual: { price: 0, planId: 'free' },
      features: [
        { text: '1 Digital Card', available: true, icon: UserPlus },
        { text: 'Basic QR Code', available: true },
        { text: 'Bio Page', available: true },
        { text: '20 Leads Only', available: true },
        { text: 'Piper Branding', available: true },
        { text: 'Visitor Notes', available: false },
        { text: 'Analytics', available: false },
        { text: 'Email Signature', available: false }
      ]
    },
    pro: {
      name: 'Pro',
      description: 'For serious professionals',
      icon: Zap,
      iconBg: 'bg-gradient-to-br from-amber-400 to-orange-500',
      iconColor: 'text-white',
      borderColor: 'border-amber-400',
      popular: true,
      monthly: { price: 299, planId: 'pro_monthly' },
      annual: { price: 2999, savings: '16%', monthlyEquiv: 249, planId: 'pro_annual' },
      features: [
        { text: 'Up to 4 Premium Cards', available: true, highlight: true },
        { text: 'Unlimited Leads', available: true, highlight: true, icon: UserPlus },
        { text: 'Visitor Notes', available: true, icon: Eye },
        { text: 'Full Analytics', available: true, icon: BarChart3 },
        { text: 'Email Signature', available: true, icon: Mail },
        { text: 'Remove Branding', available: true },
        { text: 'CRM Access', available: true },
        { text: 'Priority Support', available: true, icon: Shield }
      ],
      subtitle: 'Up to 4 cards/year'
    },
    business: {
      name: 'Business',
      description: 'For teams & organizations',
      icon: Crown,
      iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      iconColor: 'text-white',
      borderColor: 'border-emerald-400',
      monthly: { price: 350, planId: 'business_monthly' },
      annual: { price: 3500, savings: '16%', monthlyEquiv: 292, planId: 'business_annual' },
      features: [
        { text: 'Everything in Pro', available: true },
        { text: 'Team Dashboard', available: true, highlight: true, icon: Users },
        { text: 'CRM Export', available: true },
        { text: 'Custom Branding', available: true },
        { text: 'Team Analytics', available: true, icon: BarChart3 },
        { text: 'Dedicated Manager', available: true },
        { text: 'Custom Integrations', available: true },
        { text: 'Priority Support', available: true, icon: Shield }
      ],
      subtitle: 'Per card/year • Min 3 members',
      perMember: true
    }
  };

  const handleSelectPlan = (tier, period, memberCount = 1) => {
    if (!user) {
      navigate('/', { state: { showSignIn: true } });
      return;
    }
    
    const plan = plans[tier][period];
    const planDetails = {
      tier,
      period,
      planId: plan.planId,
      price: plan.price,
      memberCount: memberCount, // Number of members for Business plan
      totalAmount: plan.price * memberCount, // Total amount to charge
      name: `${plans[tier].name} ${period === 'monthly' ? 'Monthly' : 'Annual'}${tier === 'business' ? ` (${memberCount} members)` : ''}`
    };
    
    setSelectedPlan(planDetails);
    
    // Trigger payment modal
    const event = new CustomEvent('showPaymentModal', {
      detail: planDetails
    });
    window.dispatchEvent(event);
  };

  // Handle Premium Logo subscription
  const handlePremiumSubscribe = async (planId) => {
    if (!user) {
      navigate('/login?redirect=/pricing');
      return;
    }

    setSubscribingPremium(true);
    try {
      const token = await user.getIdToken();
      // Pricing page subscriptions have auto-renewal enabled
      const response = await fetch(`${API_URL}/api/subscriptions/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plan_id: planId, autoRenew: true })
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: "🎉 Welcome to Premium!",
          description: data.message
        });
      } else {
        throw new Error('Failed to subscribe');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process subscription. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubscribingPremium(false);
    }
  };

  // If pricing is disabled, show coming soon message
  if (!pricingEnabled) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="min-h-screen bg-slate-50 py-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
              <Lock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Pricing Coming Soon
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              We're finalizing our plans. Get notified when we launch!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => navigate('/')}
                className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5"
              >
                Back to Home
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = 'mailto:Contact@AllgrowTechnologies.com'}
                className="border-slate-300"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Feature row component for comparison table
  const FeatureRow = ({ feature, included, highlight }) => (
    <div className={`flex items-center gap-3 py-2 ${highlight ? 'bg-amber-50/50 -mx-2 px-2 rounded-lg' : ''}`}>
      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
        included ? 'bg-emerald-100' : 'bg-slate-100'
      }`}>
        {included ? (
          <Check className="w-3 h-3 text-emerald-600" />
        ) : (
          <X className="w-3 h-3 text-slate-400" />
        )}
      </div>
      <span className={`text-sm ${
        !included ? 'text-slate-400' : highlight ? 'font-semibold text-slate-900' : 'text-slate-700'
      }`}>
        {feature}
      </span>
    </div>
  );

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        {/* Hero Section */}
        <div className="pt-12 pb-8 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100/80 rounded-full mb-5">
              <Sparkles className="w-4 h-4 text-amber-600" />
              <span className="text-amber-700 text-sm font-medium">Simple, transparent pricing</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
              Never lose a lead again
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mb-2 max-w-2xl mx-auto">
              Each card can be assigned to a team member. If one extra deal closes, PiperCard pays for itself.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-3 mt-8">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  billingPeriod === 'monthly'
                    ? 'bg-slate-900 text-white shadow-lg'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  billingPeriod === 'annual'
                    ? 'bg-slate-900 text-white shadow-lg'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                Annual
                <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
                  -16%
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Coupon Banner (Collapsed by default) */}
        {availableCoupons.length > 0 && (
          <div className="max-w-5xl mx-auto px-4 mb-8">
            <button
              onClick={() => setShowCoupons(!showCoupons)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl hover:border-amber-300 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-500 text-white text-xs">
                  {availableCoupons.length} Active Offer{availableCoupons.length > 1 ? 's' : ''}
                </Badge>
                <span className="text-sm text-slate-700">
                  Use code <strong className="text-amber-600">{availableCoupons[0]?.code}</strong> for {availableCoupons[0]?.percentage}% off
                </span>
              </div>
              <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showCoupons ? 'rotate-90' : ''}`} />
            </button>
            
            {showCoupons && (
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {availableCoupons.map((coupon, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                    <div>
                      <span className="font-mono font-bold text-amber-600 text-sm">{coupon.code}</span>
                      <p className="text-xs text-slate-500 mt-0.5">{coupon.description}</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">{coupon.percentage}% OFF</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pricing Cards */}
        <div className="max-w-5xl mx-auto px-4 pb-16">
          <div className="grid lg:grid-cols-3 gap-6">
            {Object.entries(plans).map(([tier, plan]) => {
              const Icon = plan.icon;
              const planData = plan[billingPeriod];
              const isPopular = plan.popular;
              
              return (
                <div
                  key={tier}
                  data-testid={`pricing-card-${tier}`}
                  className={`relative rounded-2xl transition-all duration-300 ${
                    isPopular 
                      ? 'bg-white border-2 border-amber-400 shadow-xl shadow-amber-100/50 lg:scale-105 lg:-my-4' 
                      : 'bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300'
                  }`}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <div className="flex items-center gap-1 px-4 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg">
                        <Star className="w-3.5 h-3.5 fill-white" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div className="p-6 pt-8">
                    {/* Plan Header */}
                    <div className="flex items-start gap-4 mb-6">
                      <div className={`w-12 h-12 rounded-xl ${plan.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${plan.iconColor}`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                        <p className="text-sm text-slate-500">{plan.description}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-slate-900">
                          ₹{tier === 'business' ? (planData.price * businessMemberCount).toLocaleString() : planData.price.toLocaleString()}
                        </span>
                        {!plan.isFree && (
                          <span className="text-slate-500 text-sm">
                            /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                          </span>
                        )}
                      </div>
                      {plan.isFree ? (
                        <p className="text-sm text-slate-500 mt-1">Forever free</p>
                      ) : billingPeriod === 'annual' && planData.monthlyEquiv ? (
                        <p className="text-sm text-emerald-600 font-medium mt-1">
                          ₹{tier === 'business' ? (planData.monthlyEquiv * businessMemberCount).toLocaleString() : planData.monthlyEquiv}/mo billed annually
                        </p>
                      ) : null}
                      
                      {plan.subtitle && (
                        <p className="text-xs text-slate-400 mt-2">{plan.subtitle}</p>
                      )}
                    </div>

                    {/* Business Member Selector */}
                    {tier === 'business' && (
                      <div className="mb-6 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700">Team size</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setBusinessMemberCount(Math.max(3, businessMemberCount - 1))}
                              disabled={businessMemberCount <= 3}
                              className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              -
                            </button>
                            <span className="w-8 text-center font-bold text-slate-900">{businessMemberCount}</span>
                            <button
                              onClick={() => setBusinessMemberCount(businessMemberCount + 1)}
                              className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* CTA Button */}
                    <Button
                      data-testid={`pricing-cta-${tier}`}
                      onClick={() => {
                        if (plan.isFree) {
                          navigate('/shop');
                        } else if (!user) {
                          toast({
                            title: "Sign In Required",
                            description: "Please sign in to subscribe",
                            variant: "destructive"
                          });
                          window.dispatchEvent(new CustomEvent('openSignInModal'));
                        } else {
                          handleSelectPlan(tier, billingPeriod, tier === 'business' ? businessMemberCount : 1);
                        }
                      }}
                      disabled={plan.isPremiumLogo && subscribingPremium}
                      className={`w-full py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        isPopular
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-200/50'
                          : plan.isFree
                          ? 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      }`}
                    >
                      {plan.isFree ? 'Get Started Free' : 'Subscribe Now'}
                    </Button>

                    {/* Features List */}
                    <div className="mt-6 pt-6 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                        {plan.isFree ? 'Includes' : 'Everything you need'}
                      </p>
                      <div className="space-y-1">
                        {plan.features.map((feature, idx) => (
                          <FeatureRow 
                            key={idx} 
                            feature={typeof feature === 'string' ? feature : feature.text}
                            included={typeof feature === 'string' ? true : feature.available}
                            highlight={typeof feature === 'object' && feature.highlight}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trust Section */}
        <div className="bg-slate-900 py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex flex-wrap items-center justify-center gap-6 mb-6">
              <div className="flex items-center gap-2 text-slate-400">
                <Shield className="w-5 h-5" />
                <span className="text-sm">14-day money-back guarantee</span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Lock className="w-5 h-5" />
                <span className="text-sm">Secure payments via Razorpay</span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Zap className="w-5 h-5" />
                <span className="text-sm">Instant activation</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm">
              Need help or custom pricing?{' '}
              <a href="mailto:Contact@AllgrowTechnologies.com" className="text-amber-400 hover:text-amber-300 underline">
                Contact us
              </a>
            </p>
          </div>
        </div>

        {/* Card Premium Link */}
        <div className="py-8 px-4 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white rounded-xl border border-violet-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Looking for Card Premium?</p>
                  <p className="text-sm text-slate-500">Get logo branding on your QR codes</p>
                </div>
              </div>
              <Link 
                to="/card-premium" 
                className="flex items-center gap-1 text-violet-600 hover:text-violet-700 font-medium text-sm"
              >
                Learn more <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <PaymentModal />
    </Layout>
  );
};

export default Pricing;
